import type { BulkRegisterPreviewItem, FavoriteList, ListItem } from "@yutai/shared"
import useSWR, { mutate as globalMutate } from "swr"
import { trackEvent } from "../../lib/analytics.js"

// Same same-origin-in-prod / direct-to-Worker-in-dev split as lib/auth.ts.
const API_BASE = import.meta.env.DEV ? (import.meta.env.VITE_WORKER_URL ?? "") : ""
const LISTS_KEY = `${API_BASE}/api/lists`

async function apiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}/api/lists${path}`, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts?.headers ?? {}) },
  })
  if (!res.ok) {
    const body = await res.json().catch(() => null)
    const detail = body && typeof body.error === "string" ? ` (${body.error})` : ""
    throw new Error(`API error: ${res.status}${detail}`)
  }
  return res.json() as Promise<T>
}

async function fetchLists(): Promise<FavoriteList[]> {
  const res = await fetch(LISTS_KEY, { credentials: "include" })
  if (res.status === 401) return []
  if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`)
  return res.json()
}

export function refreshLists(): Promise<FavoriteList[] | undefined> {
  return globalMutate(LISTS_KEY)
}

export function useLists() {
  const { data, isLoading, mutate } = useSWR<FavoriteList[]>(LISTS_KEY, fetchLists, {
    revalidateOnFocus: false,
  })
  const lists = data ?? []

  async function createList(name: string): Promise<FavoriteList> {
    const list = await apiFetch<FavoriteList>("", {
      method: "POST",
      body: JSON.stringify({ name }),
    })
    trackEvent("list_created")
    await mutate()
    return list
  }

  async function renameList(id: string, name: string): Promise<void> {
    await apiFetch(`/${id}`, { method: "PUT", body: JSON.stringify({ name }) })
    await mutate()
  }

  async function toggleHoldingMode(id: string, isHoldingMode: boolean): Promise<void> {
    await apiFetch(`/${id}`, { method: "PUT", body: JSON.stringify({ isHoldingMode }) })
    await mutate()
  }

  async function deleteList(id: string): Promise<void> {
    try {
      await apiFetch(`/${id}`, { method: "DELETE" })
      trackEvent("list_deleted")
    } finally {
      // Resync even on failure so the UI reflects what actually got deleted.
      await mutate()
    }
  }

  // Optimistic: reorders local state immediately so the dragged item doesn't
  // snap back while the PUT is in flight, then reconciles with the server.
  async function reorderLists(orderedIds: string[]): Promise<void> {
    const reordered = orderedIds
      .map((id) => lists.find((l) => l.id === id))
      .filter((l): l is FavoriteList => Boolean(l))
    await mutate(reordered, { revalidate: false })
    await apiFetch("/reorder", { method: "PUT", body: JSON.stringify({ ids: orderedIds }) })
    await mutate()
  }

  async function quickAddToLastUsed(
    companyId: string
  ): Promise<{ list: FavoriteList; item: ListItem }> {
    const result = await apiFetch<{ list: FavoriteList; item: ListItem }>("/quick-add", {
      method: "POST",
      body: JSON.stringify({ companyId }),
    })
    trackEvent("favorite_added")
    await mutate()
    return result
  }

  async function addItem(listId: string, companyId: string): Promise<void> {
    await apiFetch(`/${listId}/items`, { method: "POST", body: JSON.stringify({ companyId }) })
    trackEvent("favorite_added")
    await mutate()
  }

  async function removeItem(listId: string, itemId: string): Promise<void> {
    try {
      await apiFetch(`/${listId}/items/${itemId}`, { method: "DELETE" })
      trackEvent("favorite_removed")
    } finally {
      await mutate()
    }
  }

  async function updateItem(
    listId: string,
    itemId: string,
    params: {
      acquisitionPrice?: number | null
      acquisitionShares?: number | null
      acquisitionDate?: string | null
      memo?: string
    }
  ): Promise<void> {
    await apiFetch(`/${listId}/items/${itemId}`, { method: "PUT", body: JSON.stringify(params) })
    await mutate()
  }

  async function reorderItems(listId: string, orderedItemIds: string[]): Promise<void> {
    const list = lists.find((l) => l.id === listId)
    if (list) {
      const reorderedItems = orderedItemIds
        .map((id) => list.items.find((i) => i.id === id))
        .filter((i): i is ListItem => Boolean(i))
      const optimistic = lists.map((l) => (l.id === listId ? { ...l, items: reorderedItems } : l))
      await mutate(optimistic, { revalidate: false })
    }
    await apiFetch(`/${listId}/items/reorder`, {
      method: "PUT",
      body: JSON.stringify({ ids: orderedItemIds }),
    })
    await mutate()
  }

  async function validateCompanyCodes(
    listId: string,
    codes: string[]
  ): Promise<BulkRegisterPreviewItem[]> {
    const { results } = await apiFetch<{ results: BulkRegisterPreviewItem[] }>(
      `/${listId}/validate-codes`,
      {
        method: "POST",
        body: JSON.stringify({ codes }),
      }
    )
    return results
  }

  async function bulkAddItems(listId: string, companyIds: string[]): Promise<number> {
    let added = 0
    for (const companyId of companyIds) {
      await apiFetch(`/${listId}/items`, { method: "POST", body: JSON.stringify({ companyId }) })
      added++
    }
    if (added > 0) {
      trackEvent("favorite_added")
      await mutate()
    }
    return added
  }

  // 証券会社CSVインポート用: 株数・取得単価を伴って登録し、保有銘柄モードを有効化する
  // （取得単価・株数はそのモードのリストでしか画面に表示・活用されないため）。
  async function bulkAddItemsWithHoldings(
    listId: string,
    items: { companyId: string; acquisitionShares: number; acquisitionPrice: number }[]
  ): Promise<number> {
    let added = 0
    for (const item of items) {
      await apiFetch(`/${listId}/items`, { method: "POST", body: JSON.stringify(item) })
      added++
    }
    if (added > 0) {
      await apiFetch(`/${listId}`, { method: "PUT", body: JSON.stringify({ isHoldingMode: true }) })
      trackEvent("favorite_added")
      await mutate()
    }
    return added
  }

  return {
    lists,
    isLoading,
    createList,
    renameList,
    toggleHoldingMode,
    deleteList,
    reorderLists,
    quickAddToLastUsed,
    addItem,
    removeItem,
    updateItem,
    reorderItems,
    validateCompanyCodes,
    bulkAddItems,
    bulkAddItemsWithHoldings,
  }
}
